// src/portals/patient/pages/BulkReviewPage.tsx

/**
 * Purpose : Answer every open item of a queue on one page instead of opening
 *           them one by one (client: "too many to click one by one"). Each item
 *           is today's own review form, stacked one after another; each card's
 *           Send/Skip submits only that item through the usual endpoint, so
 *           leaving midway is safe. Used by both queues, and by the Document
 *           Panel for one upload (?upload=).
 *
 * Contents :
 *
 * * function(BulkReviewPage) : the screen — header, progress, groups of cards
 * * function(BulkReviewCard) : one item: its full form, or a one-line summary once dealt with
 * * function(LazyMount) : mounts a card's form only when it scrolls near view
 * * function(itemTitle) : what an item is about, in a few words
 * * function(primaryUpload) : the upload an item is grouped under
 * * function(cardState) : whether a card shows its form or a summary, and which
 * * const(NO_REVIEW_ITEMS) : stable empty list while loading
 * * const(NO_DELETED) : stable empty list of deleted uploads
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "@iconify/react";
import { useMutationState, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { optionName } from "@/features/empi/empiPresentation";
import { ACTION_LABELS, isAnswerableSkipped } from "@/features/patient/human-review/components/HumanReviewQueueView";
import { QUEUE_PATHS, isFromUpload, isOpenItem, itemsForQueue, type ReviewQueue } from "@/features/patient/human-review/reviewQueues";
import { ANSWER_LABELS, ReviewItemView } from "@/features/patient/medications/components/ReviewItemView";
import { deleteUploadMutationKey, invalidatePatientData, reviewListQuery } from "@/portals/patient/api/patientQueries";
import type { ReviewItem, UploadDeletion } from "@/portals/patient/api/reviewItemsApi";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useOptionalUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { useProfileFromQuery } from "@/portals/patient/hooks/useProfileFromQuery";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { PillTabs } from "@/shared/components/PillTabs";
import { PageSpinner } from "@/shared/components/spinner";
import { cn } from "@/shared/lib/utils";


// const(NO_REVIEW_ITEMS)
const NO_REVIEW_ITEMS: ReviewItem[] = [];


// const(NO_DELETED)
const NO_DELETED: string[] = [];

type CardState =
  | { kind: "form" }
  | { kind: "answered"; text: string; canChange: boolean; changeLabel?: string }
  | { kind: "closed"; text: string };


// function(itemTitle)
/**
 * What an item is about, in a few words (the collapsed card's line).
 *
 * @param item - A review item.
 * @returns E.g. "Is this upload yours?", the medicine's name, or the record's name.
 */
export function itemTitle(item: ReviewItem): string {
  if (item.empi?.identity) {
    return item.empi.identity.uploadLabel ? `Record from “${item.empi.identity.uploadLabel}”` : "Is this upload yours?";
  }

  if (item.empi?.conflict) {
    const { resourceType, options } = item.empi.conflict;

    return optionName(resourceType, options[0]?.fields ?? {});
  }

  const subject = item.records.find((r) => r.recordRole === "INCOMING" || r.recordRole === "SUBJECT") ?? item.records[0];
  const name = subject?.fields?.medication_name;

  return typeof name === "string" && name ? name : "A medicine";
}


// function(primaryUpload)
/**
 * The upload an item is grouped under.
 *
 * @param item - A review item.
 * @returns The identity check's upload, else the newest record's / first option's; "" when unknown.
 */
export function primaryUpload(item: ReviewItem): string {
  if (item.empi?.identity?.uploadId) return item.empi.identity.uploadId;

  const incoming = item.records.find((r) => r.recordRole === "INCOMING" && r.uploadId) ?? item.records.find((r) => r.uploadId);

  if (incoming?.uploadId) return incoming.uploadId;

  return item.empi?.conflict?.options.find((o) => o.uploadId)?.uploadId ?? "";
}


// function(cardState)
/**
 * Whether a card shows its form, or a one-line summary — and which one.
 *
 * @param item - The item now (undefined once it's gone from the list).
 * @param answeredHere - The patient sent an answer on this screen.
 * @param reopened - The patient pressed "Change" to answer again.
 * @param uploadDeleted - The patient deleted this item's upload on this screen.
 * @param isActive - Whether the item is still to answer on this screen (waiting, or a Skipped one).
 * @returns The card's state.
 */
export function cardState(
  item: ReviewItem | undefined, answeredHere: boolean, reopened: boolean, uploadDeleted: boolean,
  isActive: (item: ReviewItem) => boolean = isOpenItem,
): CardState {
  if (!item || uploadDeleted) return { kind: "closed", text: "Removed with the upload" };

  const canRespond = item.canRespond ?? item.awaitingInput;

  if (reopened && canRespond) return { kind: "form" };

  if (answeredHere) {
    const last = item.patientResponses[item.patientResponses.length - 1];
    const text = last ? `Answered: ${ANSWER_LABELS[last.answer] ?? "sent"}` : "Answered";

    return { kind: "answered", text, canChange: canRespond };
  }

  // Settled by another answer of theirs ("Yes" on a file with the same name): only undoing it is left.
  if (item.triggerType === "IDENTITY_MISMATCH" && item.itemStatus === "RESOLVED_PATIENT_REPORTED" && !item.patientResponses.length) {
    return { kind: "answered", text: "Confirmed with your answer above", canChange: canRespond, changeLabel: ACTION_LABELS.notYou };
  }

  if (!isActive(item)) return { kind: "closed", text: "No longer needs your answer" };

  return { kind: "form" };
}


// function(LazyMount)
/**
 * Mounts its children only once the placeholder scrolls near view, so a long
 * list doesn't load every item's form at once. Without IntersectionObserver
 * (tests, old browsers) it mounts straight away.
 *
 * @param children - The card's form.
 * @param placeholder - What shows until then.
 * @returns The form, or the placeholder.
 */
function LazyMount({ children, placeholder }: { children: ReactNode; placeholder: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === "undefined");

  useEffect(() => {

    if (visible || !ref.current) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
    }, { rootMargin: "800px 0px" });
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [visible]);

  return <div ref={ref}>{visible ? children : placeholder}</div>;
}


// function(BulkReviewCard)
/**
 * One item of the bulk screen: today's full review form, or — once answered,
 * answered elsewhere, or removed — a one-line summary ("Change" reopens it
 * while the item still accepts answers).
 *
 * @param itemId - The item.
 * @param item - The item from the list now (undefined once it's gone).
 * @param position - "3 of 24".
 * @param state - What the card shows.
 * @param actingAsProfileId - A dependent's id when acting as them.
 * @param onDone - The form finished (sent, closed, or found answered elsewhere).
 * @param onChange - Reopen an answered card.
 * @param onItemChanged - Refresh the shared data after a change.
 * @returns The card.
 */
function BulkReviewCard({
  itemId, item, position, state, actingAsProfileId, onDone, onChange, onItemChanged,
}: {
  itemId: string;
  item: ReviewItem | undefined;
  position: string;
  state: CardState;
  actingAsProfileId?: string;
  onDone: (itemId: string) => void;
  onChange: (itemId: string) => void;
  onItemChanged: () => void;
}) {
  const title = item ? itemTitle(item) : "Removed item";

  if (state.kind !== "form") {
    return (
      <div
        id={`bulk-card-${itemId}`}
        data-testid="bulk-card-summary"
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] px-5 py-3"
      >
        <div className="flex min-w-0 items-center gap-3">
          <Icon
            icon={state.kind === "answered" ? "lucide:check-circle-2" : "lucide:circle-minus"}
            className={cn("h-5 w-5 shrink-0", state.kind === "answered" ? "text-[#16A34A]" : "text-[#9CA3AF]")}
          />
          <div className="min-w-0">
            <p className="!mb-0 truncate text-sm font-bold text-[#0A2947]">{title}</p>
            <p className="!mb-0 text-xs text-[#6B7280]">{state.text}</p>
          </div>
        </div>

        {state.kind === "answered" && state.canChange && (
          <Button
            variant="outline"
            onClick={() => onChange(itemId)}
            className="!h-8 !rounded-lg !border-[#E5E7EB] !px-3 !text-xs !font-semibold !text-[#0A2947]"
          >
            {state.changeLabel ?? ACTION_LABELS.update}
          </Button>
        )}
      </div>
    );
  }

  return (
    <section
      id={`bulk-card-${itemId}`}
      data-testid="bulk-card"
      aria-label={title}
      className="scroll-mt-28 rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm"
    >
      <p className="!mb-3 text-[11px] font-bold uppercase tracking-wide text-[#9CA3AF]">{position}</p>
      <LazyMount placeholder={<p className="!mb-0 py-10 text-center text-sm text-[#9CA3AF]">{title}</p>}>
        <ReviewItemView
          variant="stacked"
          itemId={itemId}
          actingAsProfileId={actingAsProfileId}
          onBack={() => onDone(itemId)}
          onItemChanged={onItemChanged}
        />
      </LazyMount>
    </section>
  );
}


// function(BulkReviewPage)
/**
 * /patient/human-review-queue/review-all and /patient/patient-only-queue/review-all:
 * every open item of the queue (optionally of one upload), stacked.
 *
 * @param queue - Which queue's items to show.
 * @returns The page.
 */
export function BulkReviewPage({ queue }: { queue: ReviewQueue }) {
  useProfileFromQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const uploadFilter = searchParams.get("upload");
  // The Skipped tab's items (answered late, still reaching the clinician), or the waiting ones.
  const skippedMode = searchParams.get("tab") === "skipped";
  const isActive = skippedMode ? isAnswerableSkipped : isOpenItem;
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? QUEUE_PATHS[queue].page;
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const client = useQueryClient();
  const uploadHistory = useOptionalUploadHistory();

  const list = useQuery(reviewListQuery(actingAsProfileId));
  const all = list.data ?? NO_REVIEW_ITEMS;
  const inScope = useMemo(
    () => itemsForQueue(all, queue).filter((item) => !uploadFilter || isFromUpload(item, uploadFilter)),
    [all, queue, uploadFilter],
  );
  const byId = useMemo(() => new Map(all.map((item) => [item.id, item])), [all]);

  // The cards on screen are fixed when the page opens: an answer never moves
  // the list, and new items arrive as a banner instead of shifting it.
  const [cardIds, setCardIds] = useState<string[] | null>(null);

  useEffect(() => {

    if (cardIds !== null || !list.data) return;

    setCardIds(inScope.filter(isActive).map((item) => item.id));
  }, [cardIds, list.data, inScope, isActive]);

  const newIds = cardIds ? inScope.filter((item) => isActive(item) && !cardIds.includes(item.id)).map((item) => item.id) : [];

  const [answered, setAnswered] = useState<Set<string>>(() => new Set());
  const [reopened, setReopened] = useState<Set<string>>(() => new Set());
  const [typeFilter, setTypeFilter] = useState<"all" | "medicines" | "records">("all");

  const deletedUploads = useMutationState<string>({
    filters: { mutationKey: deleteUploadMutationKey(actingAsProfileId), status: "success" },
    select: (mutation) => (mutation.state.data as UploadDeletion | undefined)?.uploadId ?? "",
  }) ?? NO_DELETED;

  const stateOf = useCallback(
    (id: string): CardState => {
      const item = byId.get(id);

      return cardState(item, answered.has(id), reopened.has(id), Boolean(item && deletedUploads.includes(primaryUpload(item))), isActive);
    },
    [byId, answered, reopened, deletedUploads, isActive],
  );

  const ids = cardIds ?? [];
  const shownIds = ids.filter((id) => {
    const item = byId.get(id);

    if (typeFilter === "all" || !item) return true;

    return typeFilter === "records" ? item.triggerType === "GOLDEN_RECORD_CONFLICT" : item.triggerType !== "GOLDEN_RECORD_CONFLICT";
  });
  const done = ids.filter((id) => stateOf(id).kind !== "form").length;

  const scrollTo = (id: string | null) => {

    if (!id) return;

    document.getElementById(`bulk-card-${id}`)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const onDone = (id: string) => {
    setAnswered((prev) => new Set(prev).add(id));
    setReopened((prev) => {
      const next = new Set(prev);
      next.delete(id);

      return next;
    });
    // After the card collapses, bring the next unanswered one into view.
    const following = shownIds.slice(shownIds.indexOf(id) + 1).find((other) => stateOf(other).kind === "form");
    setTimeout(() => scrollTo(following ?? null), 50);
  };

  const onChange = (id: string) => setReopened((prev) => new Set(prev).add(id));
  const afterChange = useCallback(() => void invalidatePatientData(client, actingAsProfileId), [client, actingAsProfileId]);

  // Grouped by upload, in the order the items came.
  const uploadLabel = (uploadId: string) => {
    const entry = uploadHistory?.zipUploads.find((u) => u.uploadId === uploadId || u.id === uploadId);

    return entry ? entry.title || entry.fileName : uploadId ? "An upload" : "Other items";
  };
  const groups: { uploadId: string; ids: string[] }[] = [];

  for (const id of shownIds) {
    const item = byId.get(id);
    const key = item ? primaryUpload(item) : "";
    const group = groups.find((g) => g.uploadId === key);

    if (group) group.ids.push(id);
    else groups.push({ uploadId: key, ids: [id] });
  }

  if (list.isError && !list.data) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center py-24 text-center">
        <p className="text-sm font-semibold text-[#F36F8F]">Couldn't load your review items. Please try again later.</p>
      </div>
    );
  }

  if (!cardIds) return <PageSpinner className="bg-[#F7F7F7]" />;

  const queueName = queue === "identity" ? "Patient Only Queue" : "Human Review Queue";

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      {/* Sticky header: back, and how many are answered. */}
      <div className="sticky top-0 z-10 -mx-4 mb-5 border-b border-[#E5E7EB] bg-[#F7F7F7]/95 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3">
          <PageBackButton onClick={() => navigate(returnTo)} ariaLabel="Back" />

          <div className="flex min-w-[220px] flex-1 items-center gap-3" data-testid="bulk-progress">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E5E7EB]">
              <div className="h-full rounded-full bg-[#E4002B]/50 transition-all" style={{ width: `${ids.length ? (done / ids.length) * 100 : 100}%` }} />
            </div>
            <p className="!mb-0 shrink-0 whitespace-nowrap text-sm font-bold text-[#0A2947]">
              {done} of {ids.length} answered
            </p>
          </div>
        </div>

        {queue === "health" && (
          <div className="mx-auto mt-3 flex max-w-4xl justify-center">
            <PillTabs
              label="Show"
              active={typeFilter}
              onChange={setTypeFilter}
              tabs={[
                { id: "all", label: "All" },
                { id: "medicines", label: "Medicines" },
                { id: "records", label: "Records" },
              ]}
            />
          </div>
        )}
      </div>

      <div className="mx-auto grid max-w-4xl gap-6">
        {newIds.length > 0 && (
          <div data-testid="bulk-new-items" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#F8C9D2] bg-[#FFF5F7] px-5 py-3">
            <p className="!mb-0 text-sm font-semibold text-[#0A2947]">
              {newIds.length} new {newIds.length === 1 ? "item needs" : "items need"} your answer.
            </p>
            <Button
              onClick={() => setCardIds([...ids, ...newIds])}
              className="!h-8 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-3 !text-xs !font-semibold !text-white hover:!bg-[#E4002B]/60 hover:!text-white"
            >
              Add them below
            </Button>
          </div>
        )}

        {ids.length === 0 ? (
          <EmptyState
            icon={<Icon icon="lucide:clipboard-check" className="h-8 w-8 text-[#9CA3AF]" />}
            title="You're all caught up"
            description={skippedMode
              ? `Nothing skipped in your ${queueName} is left to answer.`
              : `Nothing in your ${queueName} needs your answer right now.`}
            className="min-h-[300px] rounded-2xl"
          />
        ) : shownIds.length === 0 ? (
          // A filter with nothing in it: say so instead of a blank page.
          <EmptyState
            icon={<Icon icon={typeFilter === "records" ? "lucide:file-search" : "lucide:pill"} className="h-8 w-8 text-[#9CA3AF]" />}
            title={typeFilter === "records" ? "No records to review" : "No medicines to review"}
            description={typeFilter === "records"
              ? "None of your records disagree right now. Your medicines are under All or Medicines."
              : "None of your medicines need checking right now. Your records are under All or Records."}
            className="min-h-[300px] rounded-2xl"
          />
        ) : (
          groups.map((group) => (
            <section key={group.uploadId || "none"} aria-label={uploadLabel(group.uploadId)} className="grid gap-4">
              <h2 className="!mb-0 flex items-center gap-2 text-sm font-extrabold text-[#0A2947]">
                <Icon icon="lucide:file-text" className="h-4 w-4 text-[#6B7280]" />
                {uploadLabel(group.uploadId)}
              </h2>

              {group.ids.map((id) => (
                <BulkReviewCard
                  key={id}
                  itemId={id}
                  item={byId.get(id)}
                  position={`${ids.indexOf(id) + 1} of ${ids.length}`}
                  state={stateOf(id)}
                  actingAsProfileId={actingAsProfileId}
                  onDone={onDone}
                  onChange={onChange}
                  onItemChanged={afterChange}
                />
              ))}
            </section>
          ))
        )}
      </div>
    </div>
  );
}
